import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Twitter, Linkedin, Instagram, Mail, MapPin, ExternalLink } from 'lucide-react';
import Magnetic from './Magnetic';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-white/5 pt-24 pb-12 bg-zinc-950">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16 mb-20">
          {/* Brand & Social */}
          <div className="col-span-1 md:col-span-2">
            <Magnetic strength={0.15}>
              <Link to="/" className="text-2xl font-display font-bold tracking-tighter mb-8 block interactive group">
                DUOSTACK<span className="text-accent"> SYSTEMS</span>
                <span className="block text-xs text-zinc-600 font-normal mt-2 tracking-normal">
                  Building Tomorrow's Infrastructure Today
                </span>
              </Link>
            </Magnetic>
            
            <p className="text-zinc-500 text-lg max-w-sm mb-10 leading-relaxed">
              We build scalable, resilient systems that stand the test of time. Based in Accra, serving globally.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3 text-zinc-400">
                <Mail className="w-4 h-4 text-accent" />
                <a href="mailto:contact@duostacksystems.com" className="hover:text-accent transition-colors interactive">
                  contact@duostacksystems.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <MapPin className="w-4 h-4 text-accent" />
                <span>Accra, Ghana</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-3">
              {[
                { Icon: Twitter, href: "https://twitter.com/duostacksystems", label: "Twitter" },
                { Icon: Github, href: "https://github.com/duostacksystems", label: "GitHub" },
                { Icon: Linkedin, href: "https://linkedin.com/company/duostacksystems", label: "LinkedIn" },
                { Icon: Instagram, href: "https://instagram.com/duostacksystems", label: "Instagram" },
              ].map((social, i) => (
                <Magnetic key={i} strength={0.2}>
                  <a 
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-zinc-900 border border-white/5 rounded-full hover:text-accent hover:border-accent/30 hover:bg-accent/5 transition-all interactive group"
                    aria-label={`Follow us on ${social.label}`}
                  >
                    <social.Icon className="w-5 h-5" />
                  </a>
                </Magnetic>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-white mb-8">Navigation</h4>
            <ul className="space-y-4">
              {[
                { to: "/", label: "Home" },
                { to: "/services", label: "Services" },
                { to: "/projects", label: "Projects" },
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact" },
              ].map((item) => (
                <li key={item.to}>
                  <Link 
                    to={item.to} 
                    className="text-zinc-500 hover:text-accent transition-colors interactive flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-accent opacity-0 group-hover:opacity-100 transition-opacity rounded-full"></span>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-white mb-8">Resources</h4>
            <ul className="space-y-4">
              {[
                { to: "/privacy-policy", label: "Privacy Policy" },
                { to: "/terms-of-service", label: "Terms of Service" },
                { to: "/projects", label: "Case Studies" },
                { href: "https://blog.duostacksystems.com", label: "Technical Blog", external: true },
                { to: "/contact", label: "Support" },
              ].map((item) => (
                <li key={item.label}>
                  {item.external ? (
                    <a 
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-500 hover:text-accent transition-colors interactive flex items-center gap-2 group"
                    >
                      {item.label}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  ) : (
                    <Link 
                      to={item.to!} 
                      className="text-zinc-500 hover:text-accent transition-colors interactive flex items-center gap-2 group"
                    >
                      <span className="w-1 h-1 bg-accent opacity-0 group-hover:opacity-100 transition-opacity rounded-full"></span>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/5"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-zinc-950 text-xs text-zinc-600 uppercase tracking-widest">
              Built with precision
            </span>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-zinc-600">
          <div className="flex items-center gap-6 mb-4 md:mb-0">
            <p>© {currentYear} DuoStack Systems Tech Ltd. All rights reserved.</p>
            <span className="hidden md:inline text-zinc-700">•</span>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs">All systems operational</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-6 justify-center">
            <Link 
              to="/privacy-policy" 
              className="hover:text-accent transition-colors interactive text-sm"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              className="hover:text-accent transition-colors interactive text-sm"
            >
              Terms of Service
            </Link>
            <Link 
              to="/privacy-policy#cookies" 
              className="hover:text-accent transition-colors interactive text-sm"
            >
              Cookie Policy
            </Link>
            <a 
              href="mailto:legal@duostacksystems.com" 
              className="hover:text-accent transition-colors interactive text-sm"
            >
              Legal
            </a>
          </div>
        </div>

        {/* Optional: Back to Top */}
        <div className="mt-8 flex justify-center">
          <Magnetic strength={0.1}>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-xs text-zinc-700 hover:text-accent transition-colors interactive flex items-center gap-2 group"
              aria-label="Back to top"
            >
              <span className="w-6 h-[1px] bg-zinc-700 group-hover:bg-accent transition-colors"></span>
              Back to top
              <span className="w-6 h-[1px] bg-zinc-700 group-hover:bg-accent transition-colors"></span>
            </button>
          </Magnetic>
        </div>
      </div>
    </footer>
  );
};

export default Footer;